import { Link } from '@tanstack/react-router';
import { Star } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';

import CompareAddressFinder from '@/components/compare/CompareAddressFinder';
import type { DirectoryTownSummary } from '@/sanity/directory';

import type { DirectoryAgentHero_agent$key } from './__generated__/DirectoryAgentHero_agent.graphql';
import {
  formatDirectoryAgentHeroGoogleLabel,
  formatDirectoryAgentHeroHomesLabel,
  formatDirectoryAgentHeroMetaLine,
  formatDirectoryAgentHeroServicesLine,
  getDirectoryAgentHeroFeeTile,
} from '../utils/directory-agent-hero';
import { directoryBranchSectionIds } from '../utils/directory-branch-sections';
import { findTownForAreas } from '../utils/town-for-areas';

type DirectoryAgentHeroProps = {
  agent: DirectoryAgentHero_agent$key;
  towns: readonly DirectoryTownSummary[];
};

const DirectoryAgentHero = ({
  agent: agentKey,
  towns,
}: DirectoryAgentHeroProps) => {
  const agent = useFragment(
    graphql`
      fragment DirectoryAgentHero_agent on LandlordDirectoryAgent {
        name
        slug
        logoUrl
        postcode
        openedYear
        services
        googleRating
        googleReviewCount
        enrichedAt
        areas {
          slug
        }
        portalListings {
          portal
          listingCount
        }
        fees {
          management {
            percent
            amountPence
          }
          feesIncludeVat
        }
      }
    `,
    agentKey,
  );

  const portalListings = agent.portalListings;
  const managementFee = agent.fees?.management;
  const feesIncludeVat = agent.fees?.feesIncludeVat;

  const town = findTownForAreas(
    towns,
    agent.areas?.map((area) => area?.slug) ?? [],
  );
  const metaLine = formatDirectoryAgentHeroMetaLine({
    town,
    postcode: agent.postcode,
    openedYear: agent.openedYear,
  });
  const servicesLine = formatDirectoryAgentHeroServicesLine(agent.services);
  const feeTile = getDirectoryAgentHeroFeeTile({
    fee:
      managementFee == null
        ? null
        : {
            amountPence: managementFee.amountPence,
            percent: managementFee.percent,
          },
    feesIncludeVat,
  });
  const rightmovePortal = portalListings?.find(
    (listing) => listing?.portal === 'RIGHTMOVE',
  );
  const rightmoveListing =
    rightmovePortal?.listingCount == null ||
    !Number.isFinite(rightmovePortal.listingCount)
      ? null
      : { count: rightmovePortal.listingCount };
  const branchName = agent.name ?? 'this agent';

  return (
    <section className="flex flex-col gap-3.5 py-[clamp(24px,5vw,40px)] sm:gap-5">
      <nav aria-label="Breadcrumb">
        <ol className="m-0 flex list-none flex-wrap gap-2 p-0 text-[13px] text-ink-muted sm:text-sm">
          <li>
            <Link className="text-inherit hover:text-ink" to="/letting-agents">
              Letting agents
            </Link>
          </li>
          {town ? (
            <>
              <li aria-hidden="true">/</li>
              <li>
                <Link
                  className="text-inherit hover:text-ink"
                  params={{ townSlug: town.slug }}
                  to="/letting-agents/$townSlug"
                >
                  {town.name}
                </Link>
              </li>
            </>
          ) : null}
          <li aria-hidden="true" className="hidden sm:list-item">
            /
          </li>
          <li aria-current="page" className="hidden sm:list-item">
            {agent.name}
          </li>
        </ol>
      </nav>

      <div className="relative flex flex-col flex-wrap gap-8 overflow-hidden sm:flex-row sm:items-stretch sm:rounded-3xl sm:border sm:border-line sm:bg-white sm:p-8">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-[140px] -right-[120px] hidden size-[420px] rounded-full bg-accent-tint sm:block"
        />
        <div className="relative flex min-w-0 flex-[999_1_520px] flex-col">
          <span className="inline-flex w-fit rounded-full bg-chip px-2.5 py-1 text-xs text-ink-muted">
            Unclaimed · details from public sources
          </span>
          <div className="mt-3.5 flex items-center gap-3.5 sm:mt-5 sm:gap-5">
            <DirectoryAgentHeroLogo
              agentName={agent.name}
              logoUrl={agent.logoUrl}
            />
            <div className="min-w-0">
              <h1 className="m-0 text-[34px] leading-[1.02] font-medium tracking-[-0.04em] text-balance sm:text-[clamp(40px,5.4vw,64px)] sm:tracking-[-0.045em]">
                {agent.name}
              </h1>
              {metaLine ? (
                <p className="mt-1 mb-0 text-[13px] text-ink-soft sm:mt-2 sm:text-base">
                  {metaLine}
                </p>
              ) : null}
              {servicesLine ? (
                <p className="mt-0.5 mb-0 text-[13px] text-ink-muted sm:text-sm">
                  {servicesLine}
                </p>
              ) : null}
            </div>
          </div>
          <div className="mt-4 sm:mt-auto sm:pt-7">
            <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-px overflow-hidden rounded-2xl border border-line bg-line">
              {agent.googleRating != null ? (
                <DirectoryAgentHeroStat
                  href={`#${directoryBranchSectionIds.service}`}
                  subtitle={formatDirectoryAgentHeroGoogleLabel(
                    agent.googleReviewCount,
                  )}
                  value={
                    <span className="inline-flex items-center gap-1.5">
                      {agent.googleRating.toFixed(1)}
                      <Star
                        aria-hidden="true"
                        className="size-[18px] fill-accent text-ink"
                        strokeWidth={1.2}
                      />
                    </span>
                  }
                />
              ) : null}
              <DirectoryAgentHeroStat
                href={`#${directoryBranchSectionIds.cost}`}
                isMuted={feeTile.isMuted}
                subtitle={feeTile.subtitle}
                value={feeTile.value}
              />
              {rightmoveListing != null ? (
                <DirectoryAgentHeroStat
                  href={`#${directoryBranchSectionIds.market}`}
                  subtitle={formatDirectoryAgentHeroHomesLabel({
                    enrichedAt: agent.enrichedAt,
                  })}
                  value={String(rightmoveListing.count)}
                />
              ) : null}
            </div>
          </div>
        </div>

        <aside
          className="relative flex min-w-0 flex-[1_1_340px] scroll-mt-24 flex-col justify-between gap-4 rounded-[20px] bg-ink p-5 text-white sm:gap-[18px] sm:p-[26px]"
          id={directoryBranchSectionIds.act}
        >
          <div>
            <p className="m-0 text-[13px] font-medium text-accent">
              Free for landlords
            </p>
            <p className="mt-1.5 mb-0 text-[21px] leading-[1.2] font-medium tracking-[-0.02em] sm:mt-1.5 sm:text-2xl">
              Let us find you the right agent, and the right deal.
            </p>
            <p className="mt-2 mb-0 text-sm leading-relaxed text-line-strong">
              Tell us about your property. We&apos;ll compare {branchName} with
              every agent near you and negotiate fees and terms on your behalf.
            </p>
          </div>
          <div className="flex flex-col gap-2.5">
            <CompareAddressFinder
              className="gap-2.5"
              placement="directory_agent_hero"
              submitLabel="Find me the right agent"
              tone="inverse"
            />
            <p className="m-0 text-center text-[13px] text-line-strong">
              No obligation · you choose who to go with
            </p>
          </div>
        </aside>
      </div>
    </section>
  );
};

const DirectoryAgentHeroLogo = ({
  agentName,
  logoUrl,
}: {
  agentName: string | null | undefined;
  logoUrl: string | null | undefined;
}) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const [showFallback, setShowFallback] = useState(logoUrl == null);
  const initial = agentName?.trim().charAt(0).toUpperCase() || '?';

  useEffect(() => {
    if (logoUrl == null) {
      setShowFallback(true);
      return;
    }

    setShowFallback(false);
    const image = imageRef.current;
    if (image?.complete && image.naturalWidth === 0) {
      setShowFallback(true);
    }
  }, [logoUrl]);

  if (showFallback) {
    return (
      <div
        aria-label={`${agentName ?? 'Agency'} logo`}
        className="flex size-[60px] flex-none items-center justify-center rounded-2xl border border-line bg-chip text-[32px] font-semibold text-ink-muted sm:size-[88px] sm:rounded-[20px] sm:text-[32px]"
        role="img"
      >
        {initial}
      </div>
    );
  }

  return (
    <div className="flex size-[60px] flex-none items-center justify-center overflow-hidden rounded-2xl border border-line bg-white shadow-[0_1px_2px_rgb(14_14_12/0.06)] sm:size-[88px] sm:rounded-[20px]">
      <img
        alt=""
        className="max-h-full max-w-full object-contain"
        onError={() => setShowFallback(true)}
        ref={imageRef}
        src={logoUrl ?? undefined}
      />
    </div>
  );
};

const DirectoryAgentHeroStat = ({
  href,
  isMuted = false,
  subtitle,
  value,
}: {
  href: string;
  isMuted?: boolean;
  subtitle: string;
  value: ReactNode;
}) => (
  <a
    className="bg-canvas px-3.5 py-3 text-ink no-underline sm:bg-canvas sm:px-[18px] sm:py-4"
    href={href}
  >
    <div
      className={
        isMuted
          ? 'text-[22px] leading-[1.1] font-medium tracking-[-0.02em] text-ink-muted sm:text-[28px] sm:tracking-[-0.03em]'
          : 'text-[22px] leading-[1.1] font-medium tracking-[-0.02em] sm:text-[28px] sm:tracking-[-0.03em]'
      }
    >
      {value}
    </div>
    <p className="mt-1 mb-0 text-xs text-ink-muted sm:text-[13px]">
      {subtitle}
    </p>
  </a>
);

export default DirectoryAgentHero;
